import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckSquare, faSquare, faRobot, faCheck, faTimes,
    faSearch, faSpinner, faExclamationTriangle, faBuilding,
    faUniversity, faCalendar, faMoneyBill, faHashtag
} from '@fortawesome/free-solid-svg-icons';
import { fetchHMSEntities } from '../../services/hmsEntityService';
import { statementBlocks, allStatementLines, isParsedFormat } from './statementBlocks';

const BankStatementExtractionResults = ({
    extractedData,
    entitySuggestion,
    warnings = [],
    selectedLines,
    onLineToggle,
    onToggleAllLines,
    onAccept,
    onDiscard,
    onReAnalyze,
    reAnalysisGuidance,
    setReAnalysisGuidance,
}) => {
    const [entityOverride, setEntityOverride] = useState(null);
    const [entitySearch, setEntitySearch] = useState('');
    const [entityResults, setEntityResults] = useState([]);
    const [entityLoading, setEntityLoading] = useState(false);
    const [showEntityDropdown, setShowEntityDropdown] = useState(false);
    const [allEntities, setAllEntities] = useState([]);
    const [showGuidance, setShowGuidance] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchHMSEntities().then(entities => {
            setAllEntities(entities || []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowEntityDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleEntitySearch = useCallback((term) => {
        setEntitySearch(term);
        if (term.trim().length < 2) {
            setEntityResults([]);
            setShowEntityDropdown(false);
            return;
        }
        setEntityLoading(true);
        setShowEntityDropdown(true);
        const lower = term.toLowerCase();
        const filtered = allEntities.filter(e =>
            (e.label || e.hms_entity_name || e.name || '').toLowerCase().includes(lower)
        );
        setEntityResults(filtered);
        setEntityLoading(false);
    }, [allEntities]);

    const handleEntitySelect = (entity) => {
        setEntityOverride({
            entity_id: entity.value || entity.id,
            entity_name: entity.label || entity.hms_entity_name || entity.name,
        });
        setEntitySearch('');
        setShowEntityDropdown(false);
    };

    const resolvedEntity = entityOverride || (entitySuggestion?.entity_id ? {
        entity_id: entitySuggestion.entity_id,
        entity_name: entitySuggestion.entity_name,
    } : null);

    const resolvedBankAccountId = entitySuggestion?.bank_account_id || null;

    const handleAccept = () => {
        onAccept({
            hms_entity_id: resolvedEntity?.entity_id || null,
            bank_account_id: resolvedBankAccountId,
        });
    };

    const handleReAnalyze = () => {
        if (reAnalysisGuidance.trim()) {
            onReAnalyze(reAnalysisGuidance);
        }
    };

    if (!extractedData) return null;

    // A CAMT file holds one block per statement period; everything else is a
    // single block. The header reads the first block, the table reads them all.
    const blocks = statementBlocks(extractedData);
    const lines = allStatementLines(extractedData);
    const header = blocks[0] || {};
    const hasManyStatements = blocks.length > 1;
    const wasParsed = isParsedFormat(extractedData);
    const allSelected = selectedLines.length > 0 && selectedLines.every(Boolean);

    const totalDebits = lines.reduce((sum, line, idx) => {
        if (!selectedLines[idx]) return sum;
        if ((line.credit_debit || '').toUpperCase() === 'DEBIT') {
            return sum + Math.abs(parseFloat(line.amount) || 0);
        }
        return sum;
    }, 0);

    const totalCredits = lines.reduce((sum, line, idx) => {
        if (!selectedLines[idx]) return sum;
        if ((line.credit_debit || '').toUpperCase() === 'CREDIT') {
            return sum + Math.abs(parseFloat(line.amount) || 0);
        }
        return sum;
    }, 0);

    const netMovement = totalCredits - totalDebits;
    const selectedCount = selectedLines.filter(Boolean).length;

    const formatAmount = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return '-';
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return (
        <div className="bank-stmt-results">
            {/* Header info section */}
            <div className="bank-stmt-header-section">
                <div className="bank-stmt-header-title">
                    <FontAwesomeIcon icon={wasParsed ? faUniversity : faRobot} />{' '}
                    {wasParsed
                        ? 'Statement Details \u2014 read directly from the bank file, no AI'
                        : 'AI-Extracted Statement Details'}
                </div>
                <div className="bank-stmt-header-grid">
                    {header.bank_name && (
                        <div className="bank-stmt-header-field">
                            <FontAwesomeIcon icon={faUniversity} className="bank-stmt-field-icon" />
                            <div>
                                <span className="bank-stmt-field-label">Bank</span>
                                <span className="bank-stmt-field-value">{header.bank_name}</span>
                            </div>
                        </div>
                    )}
                    {!header.iban && header.account_number && (
                        <div className="bank-stmt-header-field">
                            <FontAwesomeIcon icon={faHashtag} className="bank-stmt-field-icon" />
                            <div>
                                <span className="bank-stmt-field-label">Account</span>
                                <span className="bank-stmt-field-value">
                                    {header.account_number}{header.bic ? ` (${header.bic})` : ''}
                                </span>
                            </div>
                        </div>
                    )}
                    {header.iban && (
                        <div className="bank-stmt-header-field">
                            <FontAwesomeIcon icon={faHashtag} className="bank-stmt-field-icon" />
                            <div>
                                <span className="bank-stmt-field-label">IBAN</span>
                                <span className="bank-stmt-field-value">{header.iban}</span>
                            </div>
                        </div>
                    )}
                    {header.account_holder_name && (
                        <div className="bank-stmt-header-field">
                            <FontAwesomeIcon icon={faBuilding} className="bank-stmt-field-icon" />
                            <div>
                                <span className="bank-stmt-field-label">Account Holder</span>
                                <span className="bank-stmt-field-value">{header.account_holder_name}</span>
                            </div>
                        </div>
                    )}
                    {header.statement_date && (
                        <div className="bank-stmt-header-field">
                            <FontAwesomeIcon icon={faCalendar} className="bank-stmt-field-icon" />
                            <div>
                                <span className="bank-stmt-field-label">Statement Date</span>
                                <span className="bank-stmt-field-value">{header.statement_date}</span>
                            </div>
                        </div>
                    )}
                    {header.currency && (
                        <div className="bank-stmt-header-field">
                            <FontAwesomeIcon icon={faMoneyBill} className="bank-stmt-field-icon" />
                            <div>
                                <span className="bank-stmt-field-label">Currency</span>
                                <span className="bank-stmt-field-value">{header.currency}</span>
                            </div>
                        </div>
                    )}
                    {!hasManyStatements && header.opening_balance != null && (
                        <div className="bank-stmt-header-field">
                            <div>
                                <span className="bank-stmt-field-label">Opening Balance</span>
                                <span className="bank-stmt-field-value">{formatAmount(header.opening_balance)}</span>
                            </div>
                        </div>
                    )}
                    {!hasManyStatements && header.closing_balance != null && (
                        <div className="bank-stmt-header-field">
                            <div>
                                <span className="bank-stmt-field-label">Closing Balance</span>
                                <span className="bank-stmt-field-value">{formatAmount(header.closing_balance)}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {warnings.length > 0 && (
                <div className="bank-stmt-warnings">
                    {warnings.map((warning, idx) => (
                        <div key={idx} className="bank-stmt-warning-row">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>{warning}</span>
                        </div>
                    ))}
                </div>
            )}

            {hasManyStatements && (
                <div className="bank-stmt-blocks-section">
                    <div className="bank-stmt-blocks-title">
                        This file contains {blocks.length} statements. Each is imported separately
                        so its balances reconcile on their own.
                    </div>
                    <div className="bank-stmt-blocks-strip">
                        {blocks.map((block, idx) => (
                            <div key={idx} className="bank-stmt-block-card">
                                <div className="bank-stmt-block-date">
                                    {block.statement_date || `Statement ${idx + 1}`}
                                </div>
                                {block.statement_reference && (
                                    <div className="bank-stmt-block-ref">Ref {block.statement_reference}</div>
                                )}
                                <div className="bank-stmt-block-balances">
                                    <span>Opening</span>
                                    <strong>{formatAmount(block.opening_balance)}</strong>
                                </div>
                                <div className="bank-stmt-block-balances">
                                    <span>Closing</span>
                                    <strong>{formatAmount(block.closing_balance)}</strong>
                                </div>
                                <div className="bank-stmt-block-count">
                                    {(block.lines || []).length} transactions
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Entity resolution */}
            <div className="bank-stmt-entity-section" ref={dropdownRef}>
                <div className="bank-stmt-entity-title">
                    <FontAwesomeIcon icon={faBuilding} /> Entity
                </div>
                {resolvedEntity ? (
                    <div className="bank-stmt-entity-resolved">
                        <span className="bank-stmt-entity-name">
                            {entityOverride ? 'Manual override: ' : 'Detected: '}
                            <strong>{resolvedEntity.entity_name}</strong>
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => {
                                setEntityOverride(null);
                                setShowEntityDropdown(true);
                            }}
                        >
                            Change
                        </button>
                    </div>
                ) : (
                    <div className="bank-stmt-entity-unresolved">
                        <span className="bank-stmt-entity-warning">
                            <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                            {entitySuggestion?.suggestion
                                || 'Account not matched to any IMT entity. Please select manually.'}
                        </span>
                    </div>
                )}
                <div className="bank-stmt-entity-search-wrap">
                    <div className="bank-stmt-entity-search-input">
                        <FontAwesomeIcon icon={faSearch} />
                        <input
                            type="text"
                            value={entitySearch}
                            onChange={(e) => handleEntitySearch(e.target.value)}
                            onFocus={() => {
                                if (entitySearch.trim().length >= 2) setShowEntityDropdown(true);
                            }}
                            placeholder="Search entities..."
                        />
                    </div>
                    {showEntityDropdown && entitySearch.trim().length >= 2 && (
                        <div className="bank-stmt-entity-dropdown">
                            {entityLoading ? (
                                <div className="bank-stmt-entity-dropdown-msg">
                                    <FontAwesomeIcon icon={faSpinner} spin /> Searching...
                                </div>
                            ) : entityResults.length > 0 ? (
                                entityResults.map((entity) => (
                                    <div
                                        key={entity.value || entity.id}
                                        className="bank-stmt-entity-dropdown-item"
                                        onClick={() => handleEntitySelect(entity)}
                                    >
                                        {entity.label || entity.hms_entity_name || entity.name}
                                    </div>
                                ))
                            ) : (
                                <div className="bank-stmt-entity-dropdown-msg">
                                    No entities found
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Lines table */}
            <div className="bank-stmt-lines-section">
                <div className="bank-stmt-lines-header">
                    <span className="bank-stmt-lines-title">
                        Transaction Lines ({selectedCount}/{lines.length} selected)
                    </span>
                    <button type="button" className="btn btn-sm btn-outline" onClick={onToggleAllLines}>
                        <FontAwesomeIcon icon={allSelected ? faCheckSquare : faSquare} />
                        {allSelected ? ' Deselect All' : ' Select All'}
                    </button>
                </div>
                <div className="bank-stmt-lines-table-wrap">
                    <table className="bank-stmt-lines-table">
                        <thead>
                            <tr>
                                <th style={{ width: '30px' }}></th>
                                <th style={{ width: '30px' }}>#</th>
                                <th>Date</th>
                                <th>Description</th>
                                <th>Reference</th>
                                <th>Partner</th>
                                <th className="text-right">Debit</th>
                                <th className="text-right">Credit</th>
                                <th className="text-right">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((line, idx) => {
                                const isCredit = (line.credit_debit || '').toUpperCase() === 'CREDIT';
                                return (
                                    <tr
                                        key={idx}
                                        className={selectedLines[idx] ? '' : 'bank-stmt-line-deselected'}
                                        onClick={() => onLineToggle(idx)}
                                    >
                                        <td>
                                            <FontAwesomeIcon
                                                icon={selectedLines[idx] ? faCheckSquare : faSquare}
                                                className={selectedLines[idx] ? 'text-primary' : 'text-muted'}
                                            />
                                        </td>
                                        <td>{idx + 1}</td>
                                        <td>{line.transaction_date || '-'}</td>
                                        <td className="bank-stmt-line-desc">{line.description || '-'}</td>
                                        <td>{line.reference || '-'}</td>
                                        <td>{line.partner_name || '-'}</td>
                                        <td className="text-right text-debit">
                                            {!isCredit ? formatAmount(line.amount) : ''}
                                        </td>
                                        <td className="text-right text-credit">
                                            {isCredit ? formatAmount(line.amount) : ''}
                                        </td>
                                        <td className="text-right">
                                            {line.running_balance != null ? formatAmount(line.running_balance) : ''}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* Summary row */}
                <div className="bank-stmt-summary">
                    <div className="bank-stmt-summary-item">
                        <span>Total Debits:</span>
                        <span className="text-debit">{formatAmount(totalDebits)}</span>
                    </div>
                    <div className="bank-stmt-summary-item">
                        <span>Total Credits:</span>
                        <span className="text-credit">{formatAmount(totalCredits)}</span>
                    </div>
                    <div className="bank-stmt-summary-item bank-stmt-summary-net">
                        <span>Net Movement:</span>
                        <span className={netMovement >= 0 ? 'text-credit' : 'text-debit'}>
                            {netMovement >= 0 ? '+' : ''}{formatAmount(netMovement)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Re-analyze with guidance.
                Hidden for a parsed format: guidance steers the AI extractor, and
                a camt.053 never reaches it. Leaving the control visible offers a
                button that cannot change anything. */}
            {!wasParsed && (
            <div className="bank-stmt-guidance-section">
                {!showGuidance ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => setShowGuidance(true)}
                    >
                        <FontAwesomeIcon icon={faRobot} /> Re-analyze with guidance
                    </button>
                ) : (
                    <div className="bank-stmt-guidance-input">
                        <textarea
                            value={reAnalysisGuidance}
                            onChange={(e) => setReAnalysisGuidance(e.target.value)}
                            placeholder="Provide additional context to improve extraction (e.g. 'The statement is in German, amounts in EUR, credit column is on the right')..."
                            rows={3}
                        />
                        <div className="bank-stmt-guidance-actions">
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => setShowGuidance(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={handleReAnalyze}
                                disabled={!reAnalysisGuidance.trim()}
                            >
                                <FontAwesomeIcon icon={faRobot} /> Re-analyze
                            </button>
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* Action buttons */}
            {!resolvedEntity && (
                <div className="bank-stmt-accept-blocked">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>
                        Select the IMT entity above before importing. A statement with no
                        entity cannot be listed or reconciled.
                    </span>
                </div>
            )}
            <div className="modal-actions centered">
                <button type="button" className="btn btn-secondary" onClick={onDiscard}>
                    <FontAwesomeIcon icon={faTimes} /> Discard
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAccept}
                    disabled={selectedCount === 0 || !resolvedEntity}
                    title={!resolvedEntity ? 'Select the IMT entity this statement belongs to first' : undefined}
                >
                    <FontAwesomeIcon icon={faCheck} /> Accept {selectedCount} Line{selectedCount !== 1 ? 's' : ''}
                </button>
            </div>
        </div>
    );
};

export default BankStatementExtractionResults;
